// Das Gast-Ergebnis ins Konto holen -- der Schritt, ohne den der ganze
// Trichter ein Spielzeug wäre.
//
// Wer ohne Konto vektorisiert hat, sein Ergebnis gesehen hat und sich dann
// anmeldet, stünde sonst in einem leeren Dashboard: die Arbeit läge weiter im
// Browser, das Konto wüsste nichts davon, und der erste Eindruck wäre
// „nochmal von vorn". Diese Datei läuft deshalb auf **jeder** Seite, sobald
// eine Sitzung da ist (`entrypoints/application.ts`), und schiebt nach, was
// noch offen ist.
//
// Zwei Zusagen, beide hier und nicht im Aufrufer:
//
// * **Idempotent.** Der Schlüssel des Ergebnisses (`GuestResult#key`) geht
//   mit und steht auf dem Server als `projects.guest_import_key`. Ein
//   zweiter Aufruf legt nichts Neues an, sondern bekommt das Projekt von
//   vorhin -- zwei Tabs, ein Neuladen mitten im Hochladen, ein zweiter Anlauf
//   nach einem Netzfehler sind damit alle derselbe Fall. Lokal wird der
//   Eintrag erst gelöscht, **nachdem** der Server ihn bestätigt hat.
// * **Ohne Quote.** Die Übernahme zählt keine Vektorisierung -- die ist
//   längst gelaufen und war längst bezahlt. Das entscheidet der Server
//   (Api::V1::GuestImportsController); hier steht es, damit niemand auf die
//   Idee kommt, die Übernahme über den gewöhnlichen Upload zu schicken.

import type { GuestResult, GuestResultStorage } from '../editor/guestResult'

export type ImportedProject = { id: number; name: string }

/**
 * Wie lange ein Gast-Ergebnis auf ein Konto wartet.
 *
 * Der Eintrag im Browser ist der ganze Nachweis -- der Server hat nie etwas
 * gespeichert und kann weder Gültigkeit noch Alter prüfen; das muss hier
 * passieren, bevor irgendetwas geschickt wird. Dreißig Tage decken „ich lege
 * das Konto am Wochenende an" mit Luft. Was älter ist, wird nicht mehr zum
 * Projekt: ein Motiv, das jemand vor Monaten auf einem geteilten Rechner
 * probiert hat, soll beim nächsten Anmelden dort nicht als „Mein erstes
 * Motiv" in einem Konto auftauchen, das es nie gesehen hat. Deutlich länger
 * als die wartende Datei (pendingLaunch.ts, ein Tag), weil das hier kein
 * Slot der Tagesquote kostet und der Gast das Ergebnis bewusst gesehen hat.
 */
export const GUEST_RESULT_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Ob ein Ergebnis noch gilt. Eine zurückgestellte Uhr lässt `now` vor
 * `createdAt` liegen -- das Alter ist dann unbekannt, nicht groß (dieselbe
 * Regel wie `isFreshLaunch`).
 */
export function isFreshGuestResult(createdAt: number, now: number): boolean {
  return now - createdAt < GUEST_RESULT_TTL_MS
}

/** Was der Server tut -- als Schnittstelle, damit vitest ohne fetch auskommt. */
export type GuestImportApi = (result: GuestResult) => Promise<ImportedProject>

export type GuestImportOutcome = {
  imported: ImportedProject[]
  /** Was liegen bleiben musste; beim nächsten Seitenaufruf ist es wieder dran. */
  pending: number
  /** Was zu alt war und deshalb gelöscht statt geschickt wurde. */
  expired: number
}

/**
 * Alles übernehmen, was im Browser noch liegt.
 *
 * Ein Fehlschlag lässt den Eintrag stehen und bricht die Runde nicht ab: das
 * eine Ergebnis, an dem der Server sich verschluckt, soll das nächste nicht
 * mit in den Abgrund ziehen.
 */
export async function importGuestResults(
  storage: GuestResultStorage,
  send: GuestImportApi,
  now: () => number = Date.now,
): Promise<GuestImportOutcome> {
  const pending = await storage.all()
  const imported: ImportedProject[] = []
  let left = 0
  let expired = 0

  // Der Reihe nach und nicht parallel: zwei gleichzeitige Übernahmen aus
  // demselben Browser wären genau das Rennen, das der eindeutige Index auf
  // dem Server abfangen muss -- eines nach dem anderen lässt es gar nicht
  // erst entstehen.
  for (const result of [...pending].sort((a, b) => a.createdAt - b.createdAt)) {
    if (!isFreshGuestResult(result.createdAt, now())) {
      await storage.delete(result.key)
      expired += 1
      continue
    }

    try {
      const project = await send(result)
      imported.push(project)
      await storage.delete(result.key)
    } catch {
      left += 1
    }
  }

  return { imported, pending: left, expired }
}
